import type { EndpointResponse } from '@croissants/contracts';
import { internal } from '@croissants/contracts';
import {
  type GuestPreset,
  guestColumnOf,
  guestPresetsFor,
  type PayerPreset,
  payersOf,
  rowLocks,
  type SupportReasonKind,
  type SupportTable,
  type SupportTableContext,
  type SupportTableRow,
  suggestedPayer,
} from '@croissants/contracts/projections';
import type { InternalStaticKey } from '../i18n/index';
import { useEndpointQuery } from './api';

/**
 * The Support screens' shared pieces (issue #2272): the open-case count the
 * rail and the Cases tab read, a case's address, and the edits a person makes
 * to the decision table. Every amount comes from the one table module,
 * `@croissants/contracts/projections` (support-case-table.ts): the presets fill
 * the cells, the locks say which cells fill themselves, and nothing here works
 * out a figure of its own.
 */

export type SupportCaseView = EndpointResponse<typeof internal.getSupportCase>;
export type SupportCaseRecord = SupportCaseView['case'];
export type PaymentMovement = EndpointResponse<
  typeof internal.listPaymentMovements
>['items'][number];

/** One query supplies the rail's badge and the Cases tab's count: the counts alone. */
export function useSupportCounts() {
  return useEndpointQuery(internal.getSupportCounts, {}, { refetchInterval: 15_000 });
}

export function supportCaseHref(caseId: string): string {
  return `/support/cases/${caseId}`;
}

/** A reason as the Reason dropdown names it; the case's title is made of these. */
export const REASON_LABEL: Readonly<Record<SupportReasonKind, InternalStaticKey>> = {
  REMOVED: 'support.reason.REMOVED',
  LOST: 'support.reason.LOST',
  CANCELLED: 'support.reason.CANCELLED',
  COMPLAINT: 'support.reason.COMPLAINT',
  DISPUTE: 'support.reason.DISPUTE',
  FRAUD: 'support.reason.FRAUD',
  STRIPE: 'support.reason.STRIPE',
  OTHER: 'support.reason.OTHER',
  WASTED_RIDER: 'support.reason.WASTED_RIDER',
  REFUND_REFUSED: 'support.reason.REFUND_REFUSED',
};

/** The case's title: each reason's name, an Other reason's own text in its place. */
export function caseTitle(
  record: Pick<SupportCaseRecord, 'reasons'>,
  t: (key: InternalStaticKey) => string,
): string {
  return record.reasons
    .map((reason) =>
      reason.kind === 'OTHER' && reason.text !== null && reason.text !== ''
        ? reason.text
        : t(REASON_LABEL[reason.kind]),
    )
    .join(' · ');
}

/** A table cell: two decimals and no symbol; a zero is a dash. */
export function cellAmount(minorUnits: number): string {
  if (minorUnits === 0) return '–';
  const sign = minorUnits < 0 ? '−' : '';
  return `${sign}${(Math.abs(minorUnits) / 100).toFixed(2)}`;
}

/** What a person typed in a cell, in cents: anything that is not a positive amount is zero. */
export function parseAmount(raw: string): number {
  const value = Number(raw.trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
}

/** The guest preset a table keeps after the case changed under it (a report, a lock). */
function keptGuestPreset(context: SupportTableContext, current: GuestPreset): GuestPreset {
  const allowed = guestPresetsFor(context);
  if (current !== 'CUSTOM' && allowed.includes(current)) return current;
  return allowed[0] ?? 'NOTHING';
}

/**
 * The table again after the server answered with new rows or a new lock: the
 * same presets where they still apply, the first allowed guest preset where
 * not, and a typed split back to the suggested payer.
 */
export function refilledTable(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  reasons: readonly SupportReasonKind[],
  current: SupportTable,
): SupportTable {
  const guestPreset = keptGuestPreset(context, current.guestPreset);
  const payerPreset =
    current.payerPreset === 'CUSTOM' ? suggestedPayer(reasons) : current.payerPreset;
  return withGuestPreset(
    rows,
    context,
    { guestPreset, payerPreset, rows: current.rows },
    guestPreset,
  );
}

/** A guest preset clicked: its column, and the payer preset applied to it (a typed split clears). */
export function withGuestPreset(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  current: SupportTable,
  preset: GuestPreset,
): SupportTable {
  const out = guestColumnOf(rows, context, preset);
  const payerPreset = current.payerPreset === 'CUSTOM' ? null : current.payerPreset;
  return {
    guestPreset: preset,
    payerPreset,
    rows: rows.map((row) => {
      const outMinor = out.get(row.key) ?? 0;
      return { key: row.key, outMinor, ...payersOf(row, outMinor, payerPreset) };
    }),
  };
}

/** A payer preset clicked: who pays each row's amount, the guest column untouched. */
export function withPayerPreset(
  rows: readonly SupportTableRow[],
  current: SupportTable,
  preset: PayerPreset,
): SupportTable {
  return {
    ...current,
    payerPreset: preset,
    rows: rows.map((row) => {
      const outMinor = current.rows.find((cell) => cell.key === row.key)?.outMinor ?? 0;
      return { key: row.key, outMinor, ...payersOf(row, outMinor, preset) };
    }),
  };
}

/** A product row whose payer cells all fill themselves: the bakery never supplied it. */
export function payersFillThemselves(row: SupportTableRow): boolean {
  return row.kind === 'PRODUCT' && rowLocks(row, 0, 0).bakeryFixed !== null;
}

/**
 * A guest cell typed (an unlocked case): the guest column becomes Custom. A
 * payer preset still applies to the new amount; a typed split is kept, except
 * that a row where nothing goes out pays nothing and a row that fills itself
 * fills itself again.
 */
export function withOut(
  row: SupportTableRow,
  current: SupportTable,
  outMinor: number,
): SupportTable {
  const preset = current.payerPreset === 'CUSTOM' ? null : current.payerPreset;
  return {
    ...current,
    guestPreset: 'CUSTOM',
    rows: current.rows.map((cell) => {
      if (cell.key !== row.key) return cell;
      if (current.payerPreset !== 'CUSTOM' || outMinor === 0 || payersFillThemselves(row))
        return { key: cell.key, outMinor, ...payersOf(row, outMinor, preset) };
      return { ...cell, outMinor };
    }),
  };
}

export type PayerColumn = 'bakeryMinor' | 'courierMinor' | 'croissantMinor';

/** A payer cell typed: the split becomes Custom. */
export function withPayer(
  key: string,
  current: SupportTable,
  column: PayerColumn,
  minorUnits: number,
): SupportTable {
  return {
    ...current,
    payerPreset: 'CUSTOM',
    rows: current.rows.map((cell) => (cell.key === key ? { ...cell, [column]: minorUnits } : cell)),
  };
}

/**
 * How far one instruction got, out of four: decided, sent, Stripe accepted (it
 * answered with an object), Stripe confirmed.
 */
export function movementSteps(movement: Pick<PaymentMovement, 'status' | 'stripeRef'>): number {
  if (movement.status === 'CONFIRMED') return 4;
  if (movement.stripeRef !== null) return 3;
  return movement.status === 'SENT' ? 2 : 1;
}
